/**
 * Source-agnostic alignment model + station math.
 *
 * All geometry is Float64 in project world coordinates (Z-up). Stations
 * are HORIZONTAL (2D) lengths from the first vertex ("internal"); what
 * the user sees is the "display" station = staStart + internal with
 * station equations applied. No three.js here so this stays cheap to
 * unit-test and free of float32 jitter at UTM scale.
 */

export type KeyPointKind = 'START' | 'END' | 'PC' | 'PT' | 'TS' | 'SC' | 'CS' | 'ST' | 'PI' | 'VERTEX';

/** `(x, y) → z` on a loaded TIN (or any other surface source). Returns null off-surface. */
export type DrapeSampler = (x: number, y: number) => number | null;

export interface StationEquation {
  /** Internal station (2D length from the first vertex) where the equation sits. */
  staInternal: number;
  /** Display station shown from this point onwards. */
  staAhead: number;
}

export interface KeyPoint {
  /** Internal station. */
  station: number;
  kind: KeyPointKind;
}

/**
 * A geometry element the parser could not reproduce and drew as a straight
 * chord instead: a `<Spiral>` whose `spiType` is not the clothoid we
 * integrate, or a `<Curve>` with neither a `<Center>` nor a radius that
 * yields one. The alignment is still usable — it is just short of the corner
 * over this stretch, which matters to anything measured against it (station
 * volumes, sections, offsets), so it travels with the alignment instead of
 * living only in a `console.warn`.
 */
export type AlignmentDegradationKind = 'SPIRAL_CHORD' | 'CURVE_CHORD';

export interface AlignmentDegradation {
  kind: AlignmentDegradationKind;
  /** The offending element as the file named it, e.g. `Spiral (biquadratic)`. */
  element: string;
  /** Internal station (2D metres from the first vertex) where the element starts. */
  staInternalM: number;
  /** Length of the chord that replaced it (m). */
  lengthM: number;
}

export interface Alignment {
  /** `${layerId}:${featureId}` — see alignmentKey(). */
  key: string;
  source: 'LANDXML' | 'FEATURE';
  layerId: string;
  featureId: string;
  name: string;
  /** Flattened world XYZ: [x0,y0,z0,x1,y1,z1,...]. Consecutive XY duplicates removed. */
  points: Float64Array;
  /** Internal station per vertex (2D). */
  cumLengths: Float64Array;
  /** = cumLengths[last]. */
  length: number;
  staStart: number;
  /** Sorted by staInternal. */
  equations: StationEquation[];
  /** Sorted by station; always contains START (0) and END (length). */
  keyPoints: KeyPoint[];
  /**
   * Elements drawn as straight chords instead of their true geometry, in
   * station order. Empty for a faithfully tessellated alignment — and always
   * empty for GeoJSON/feature alignments, whose vertices ARE the geometry.
   */
  degradations: AlignmentDegradation[];
}

/**
 * `degradations` is optional here (not just `keyPoints`) so every builder that
 * has nothing to report — the feature/GeoJSON path, tests, fixtures — keeps
 * working unchanged and gets `[]`.
 */
export type AlignmentInit = Omit<Alignment, 'cumLengths' | 'length' | 'keyPoints' | 'degradations'> & {
  keyPoints?: KeyPoint[];
  degradations?: AlignmentDegradation[];
};

const EPS = 1e-9;

export const alignmentKey = (layerId: string, featureId: string) => `${layerId}:${featureId}`;

export function buildAlignment(init: AlignmentInit): Alignment | null {
  const src = init.points;
  // Drop consecutive XY duplicates (zero-length segments break projection).
  const kept: number[] = [];
  for (let i = 0; i + 2 < src.length; i += 3) {
    const n = kept.length;
    if (n >= 3 && Math.abs(kept[n - 3] - src[i]) < EPS && Math.abs(kept[n - 2] - src[i + 1]) < EPS) continue;
    kept.push(src[i], src[i + 1], src[i + 2]);
  }
  const count = kept.length / 3;
  if (count < 2) return null;
  const points = new Float64Array(kept);
  const cumLengths = new Float64Array(count);
  for (let i = 1; i < count; i++) {
    const dx = points[i * 3] - points[(i - 1) * 3];
    const dy = points[i * 3 + 1] - points[(i - 1) * 3 + 1];
    cumLengths[i] = cumLengths[i - 1] + Math.hypot(dx, dy);
  }
  const length = cumLengths[count - 1];
  const kps: KeyPoint[] = (init.keyPoints ?? []).filter((k) => k.station > EPS && k.station < length - EPS);
  kps.push({ station: 0, kind: 'START' }, { station: length, kind: 'END' });
  kps.sort((a, b) => a.station - b.station);
  const equations = [...init.equations].sort((a, b) => a.staInternal - b.staInternal);
  return { ...init, points, cumLengths, length, keyPoints: kps, equations, degradations: init.degradations ?? [] };
}

/** Index i such that cumLengths[i] <= station < cumLengths[i+1] (clamped to a valid segment). */
function segmentIndex(a: Alignment, station: number): number {
  const c = a.cumLengths;
  const n = c.length;
  if (station <= 0) return 0;
  if (station >= a.length) return n - 2;
  let lo = 0;
  let hi = n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (c[mid] <= station) lo = mid;
    else hi = mid;
  }
  return lo;
}

export function pointAtStation(a: Alignment, station: number) {
  const s = Math.min(Math.max(station, 0), a.length);
  const i = segmentIndex(a, s);
  const p = a.points;
  const ax = p[i * 3], ay = p[i * 3 + 1], az = p[i * 3 + 2];
  const bx = p[i * 3 + 3], by = p[i * 3 + 4], bz = p[i * 3 + 5];
  const segLen = a.cumLengths[i + 1] - a.cumLengths[i];
  const t = segLen > EPS ? (s - a.cumLengths[i]) / segLen : 0;
  const dx = bx - ax, dy = by - ay, dz = bz - az;
  // Normalize by the HORIZONTAL (2D) segment length, not the 3D chord length,
  // so (tx,ty) is a unit 2D heading and tz == dz/ds matches gradeAtStation().
  const norm = segLen > EPS ? segLen : 1;
  return {
    x: ax + dx * t,
    y: ay + dy * t,
    z: az + dz * t,
    tx: dx / norm,
    ty: dy / norm,
    tz: dz / norm,
  };
}

export function projectPoint(a: Alignment, x: number, y: number, z: number) {
  const p = a.points;
  const n = p.length / 3;
  let best: { station: number; offset: number; dz: number } | null = null;
  for (let i = 0; i < n - 1; i++) {
    const ax = p[i * 3], ay = p[i * 3 + 1], az = p[i * 3 + 2];
    const bx = p[i * 3 + 3], by = p[i * 3 + 4], bz = p[i * 3 + 5];
    const dx = bx - ax, dy = by - ay;
    const segLen2 = dx * dx + dy * dy;
    if (segLen2 < EPS) continue;
    const t = Math.min(1, Math.max(0, ((x - ax) * dx + (y - ay) * dy) / segLen2));
    const cx = ax + dx * t, cy = ay + dy * t, cz = az + (bz - az) * t;
    const dist = Math.hypot(x - cx, y - cy);
    if (!best || dist < Math.abs(best.offset)) {
      // cross(tangent, p - a) > 0 → point is to the LEFT → negative offset
      const cross = dx * (y - ay) - dy * (x - ax);
      const segLen = Math.sqrt(segLen2);
      best = { station: a.cumLengths[i] + segLen * t, offset: cross > 0 ? -dist : dist, dz: z - cz };
    }
  }
  return best;
}

export function gradeAtStation(a: Alignment, station: number): number {
  const i = segmentIndex(a, Math.min(Math.max(station, 0), a.length));
  const ds = a.cumLengths[i + 1] - a.cumLengths[i];
  if (ds < EPS) return 0;
  return (a.points[i * 3 + 5] - a.points[i * 3 + 2]) / ds;
}

/** Accumulated display shift introduced by all equations at or before `station`. Equations must be sorted by staInternal. */
function shiftAtRaw(station: number, staStart: number, equations: StationEquation[]): number {
  let shift = 0;
  for (const eq of equations) {
    if (eq.staInternal > station) break;
    const back = staStart + eq.staInternal + shift;
    shift += eq.staAhead - back;
  }
  return shift;
}

export function internalToDisplayRaw(station: number, staStart: number, equations: StationEquation[]): number {
  return staStart + station + shiftAtRaw(station, staStart, equations);
}

export function displayToInternalRaw(
  display: number,
  staStart: number,
  equations: StationEquation[],
  length: number,
): number | null {
  // Regions: [0, eq0), [eq0, eq1), ... [eqN, length]. Each has a constant shift.
  const bounds = [0, ...equations.map((e) => e.staInternal), length];
  for (let r = 0; r < bounds.length - 1; r++) {
    const lo = bounds[r];
    const hi = bounds[r + 1];
    if (hi < lo) continue;
    const internal = display - staStart - shiftAtRaw(lo, staStart, equations);
    const last = r === bounds.length - 2;
    if (internal >= lo - EPS && (last ? internal <= hi + EPS : internal < hi - EPS)) {
      return Math.min(Math.max(internal, 0), length);
    }
  }
  return null;
}

export function internalToDisplay(a: Alignment, station: number): number {
  return internalToDisplayRaw(station, a.staStart, a.equations);
}

export function displayToInternal(a: Alignment, display: number): number | null {
  return displayToInternalRaw(display, a.staStart, a.equations, a.length);
}

export function formatStation(display: number, decimals = 2): string {
  const sign = display < 0 ? '-' : '';
  const abs = Math.abs(display);
  const major = Math.floor(abs / 1000);
  const minor = abs - major * 1000;
  const minorStr = minor.toFixed(decimals).padStart(decimals > 0 ? 4 + decimals : 3, '0');
  return `${sign}STA ${major}+${minorStr}`;
}

/**
 * "spiral at STA 0+100.00, curve at STA 0+150.00" — an alignment's chord
 * fallbacks as one line, at the DISPLAY stations the user reads everywhere
 * else (staStart + equations applied). Empty string when there are none.
 *
 * `t` is i18next's translator, taken as a parameter because this module stays
 * free of React and i18n wiring. It is called for two keys per degradation:
 * `alignments.degraded.kindSpiral`/`kindCurve` for the noun, then
 * `alignments.degraded.item` with `{ kind, station }`.
 *
 * One formatter, three call sites — `AlignmentWalkToolbar`'s warning glyph,
 * `LandXMLProperties`' alignment section and `StationVolumesFields`' note —
 * which were three verbatim copies before (fix round 1).
 */
export function degradationList(
  a: Alignment,
  t: (key: string, vars?: Record<string, unknown>) => string,
): string {
  return a.degradations
    .map((d) =>
      t('alignments.degraded.item', {
        kind: t(d.kind === 'SPIRAL_CHORD' ? 'alignments.degraded.kindSpiral' : 'alignments.degraded.kindCurve'),
        station: formatStation(internalToDisplay(a, d.staInternalM)),
      }),
    )
    .join(', ');
}

export function parseStation(text: string): number | null {
  const s = text.replace(/sta/i, '').replace(/\s+/g, '');
  if (!s) return null;
  const neg = s.startsWith('-');
  const body = neg ? s.slice(1) : s;
  let value: number;
  if (body.includes('+')) {
    const [maj, min] = body.split('+');
    const m = Number(maj);
    const mm = Number(min);
    if (!Number.isFinite(m) || !Number.isFinite(mm) || min === '') return null;
    value = m * 1000 + mm;
  } else {
    value = Number(body);
    if (!Number.isFinite(value)) return null;
  }
  return neg ? -value : value;
}

export function nextKeyPoint(a: Alignment, station: number, dir: 1 | -1): number | null {
  if (dir === 1) {
    for (const k of a.keyPoints) if (k.station > station + 1e-6) return k.station;
    return null;
  }
  for (let i = a.keyPoints.length - 1; i >= 0; i--) if (a.keyPoints[i].station < station - 1e-6) return a.keyPoints[i].station;
  return null;
}

export function concatMultiLine(parts: ReadonlyArray<ReadonlyArray<ReadonlyArray<number>>>): Float64Array {
  const out: number[] = [];
  for (const part of parts) {
    for (const c of part) {
      const x = c[0], y = c[1], z = c[2] ?? 0;
      const n = out.length;
      if (n >= 3 && Math.abs(out[n - 3] - x) < EPS && Math.abs(out[n - 2] - y) < EPS && Math.abs(out[n - 1] - z) < EPS) continue;
      out.push(x, y, z);
    }
  }
  return new Float64Array(out);
}

export function reversePoints(points: Float64Array): Float64Array {
  const n = points.length / 3;
  const out = new Float64Array(points.length);
  for (let i = 0; i < n; i++) {
    const j = n - 1 - i;
    out[i * 3] = points[j * 3];
    out[i * 3 + 1] = points[j * 3 + 1];
    out[i * 3 + 2] = points[j * 3 + 2];
  }
  return out;
}

export function drapePoints(points: Float64Array, sampler: (x: number, y: number) => number | null): Float64Array {
  const out = new Float64Array(points);
  for (let i = 0; i + 2 < out.length; i += 3) {
    const z = sampler(out[i], out[i + 1]);
    if (z !== null && Number.isFinite(z)) out[i + 2] = z;
  }
  return out;
}

export function isFlat(points: Float64Array): boolean {
  if (points.length < 3) return true;
  const z0 = points[2];
  for (let i = 5; i < points.length; i += 3) if (Math.abs(points[i] - z0) > 1e-6) return false;
  return true;
}

export interface ClipDims {
  /** Box extent along the alignment (m). */
  length: number;
  /** Box extent across the alignment (m). */
  width: number;
  /** Vertical extent (m). */
  height: number;
  /**
   * Metres to shift the box across the alignment, **positive = right** when
   * looking up-station (spec §11.1) — the same sign `projectPoint` gives an
   * offset. `clipBoxAtStation` is centred on the centreline, which is wrong
   * for an asymmetric swath: a 5 L / 15 R corridor's slab has to sit 5 m
   * right of centre or it clips away the widening it exists to show.
   * Optional, default 0, so the walk toolbar's own centred behaviour is
   * unchanged.
   */
  lateralOffset?: number;
  /**
   * Elevation of the box centre (m). Defaults to the alignment's own z at the
   * station, which is only meaningful when the alignment carries a vertical
   * profile: a flat alignment (`isFlat`) sits at a constant z with no relation
   * to the ground, so a slab centred there clips the whole cloud away and the
   * viewer goes blank with nothing to explain it (ruling D35). The station
   * volumes' own surfaces know better — see `clipDimsForRun`.
   */
  verticalCentre?: number;
}

/**
 * Clip-box transform for a cross-section slice at `station`: centred on the
 * alignment point — shifted by `dims.lateralOffset` along the right normal and
 * lifted to `dims.verticalCentre` when either is given — with box +X along the
 * tangent, +Y across (left), +Z up. The
 * rotation is a pure yaw about Z (scene is Z-up) so the slice stays vertical
 * regardless of grade. Returned in the `ClipBoxState` shape (quaternion xyzw).
 */
export function clipBoxAtStation(a: Alignment, station: number, dims: ClipDims) {
  const p = pointAtStation(a, station);
  const yaw = Math.atan2(p.ty, p.tx);
  const s = Math.sin(yaw / 2);
  const c = Math.cos(yaw / 2);
  // The RIGHT normal is (ty, -tx): `projectPoint`'s convention is
  // cross(tangent, p - a) > 0 -> LEFT -> negative offset, so the positive
  // offset direction — right — is the tangent rotated -90 degrees.
  const off = dims.lateralOffset ?? 0;
  return {
    center: [p.x + p.ty * off, p.y - p.tx * off, dims.verticalCentre ?? p.z] as [number, number, number],
    halfSize: [dims.length / 2, dims.width / 2, dims.height / 2] as [number, number, number],
    rotation: [0, 0, s, c] as [number, number, number, number],
  };
}
